import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuiz } from '../hooks/useQuiz';
import { QuizHeader } from '../components/quiz/QuizHeader';
import { QuizAnswers } from '../components/quiz/QuizAnswers';
import { QuizNavigation } from '../components/quiz/QuizNavigation';
import { TopBar } from '../components/layout/TopBar';
import { QuizLoading } from '../components/quiz/QuizLoading';

// Nasconde la barra di scorrimento del browser
const hideScrollbar = () => {
  const style = document.createElement('style');
  style.textContent = `
    html, body {
      overflow: hidden !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
    }
    html::-webkit-scrollbar, body::-webkit-scrollbar {
      display: none !important;
    }
    /* Nasconde anche le barre di scorrimento interne */
    * {
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
    }
    *::-webkit-scrollbar {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
};

// Funzione per calcolare le risposte errate localmente
const calculateWrongAnswers = (answers: any[], questions: any[]) => {
  const wrongAnswers: any[] = [];
  
  answers.forEach((answer, answerIndex) => {
    const question = questions.find(q => q.id === answer.questionId);
    if (question) {
      const selectedOption = question.answerOptions.find((opt: any) => opt.id === answer.selectedAnswerId);
      const correctOption = question.answerOptions.find((opt: any) => opt.isCorrect);
      
      if (selectedOption && correctOption && selectedOption.id !== correctOption.id) {
        wrongAnswers.push({
          questionId: question.id,
          questionNumber: answerIndex + 1, // Numero originale della domanda nel quiz
          question: question,
          selectedAnswer: selectedOption,
          correctAnswer: correctOption,
          timeSpent: answer.timeSpent
        });
      }
    }
  });
  
  // Restituisce direttamente l'array per essere coerente con l'API
  return wrongAnswers;
};

export const Quiz: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isReset = searchParams.get('reset') === 'true';
  const { 
    session, 
    currentQuestionIndex, 
    timeSpent, 
    isSubmitting,
    error,
    loadSession, 
    submitAnswer, 
    nextQuestion, 
    previousQuestion, 
    // finishQuiz, // Rimuoviamo perché non utilizzato
    finishQuizWithAnswers,
    resetLocalState,
    getCurrentQuestion,
    getProgress,
    isLastQuestion,
    isFirstQuestion,
    isQuestionAnswered,
    getAnswerForQuestion,
    getSelectedAnswerForQuestion,
    getAllAnswers
  } = useQuiz();

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [isLoading, setIsLoading] = useState(true);

  // Carica sessione al mount
  useEffect(() => {
    // Nasconde la barra di scorrimento del browser
    hideScrollbar();
    
    const loadQuizSession = async () => {
      if (!sessionId) {
        navigate('/subjects');
        return;
      }

      try {
        // Carica sessione dal backend
        await loadSession(sessionId);
        
        // Se è un reset, resetta lo stato locale
        if (isReset) {
          console.log('🔄 [DEBUG] Reset stato locale per riutilizzo sessione');
          resetLocalState();
          
          // Rimuovi il parametro reset dall'URL
          navigate(`/quiz/${sessionId}`, { replace: true });
        }
        
        // Imposta risposta già data se presente (solo se non è un reset)
        const currentQuestion = getCurrentQuestion();
        if (currentQuestion && isQuestionAnswered(currentQuestion.id) && !isReset) {
          const answer = getAnswerForQuestion(currentQuestion.id);
          if (answer) {
            setSelectedAnswer(answer.selectedAnswerId);
          }
        } else {
          setSelectedAnswer(null);
        }
      } catch (error) {
        console.error('Errore caricamento sessione:', error);
        navigate('/subjects');
      } finally {
        setIsLoading(false);
      }
    };

    loadQuizSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]); // Rimuoviamo le dipendenze che causano il loop infinito

  // Timer per domanda corrente
  useEffect(() => {
    if (session && session.status === 'active') {
      setQuestionStartTime(Date.now());
    }
  }, [currentQuestionIndex, session]);

  // Timer globale
  useEffect(() => {
    if (!session || session.status !== 'active') return;

    const timer = setInterval(() => {
      // Il timer è gestito dall'hook useQuiz
    }, 1000);

    return () => clearInterval(timer);
  }, [session]);

  const handleAnswerSelect = (answerId: string) => {
    setSelectedAnswer(answerId);
  };

  const handleNext = async () => {
    if (!session || !selectedAnswer) return;

    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return;

    try {
      const timeSpentOnQuestion = Date.now() - questionStartTime;
      
      console.log('🔄 [DEBUG] Salvando risposta:', {
        questionId: currentQuestion.id,
        answerId: selectedAnswer,
        timeSpent: timeSpentOnQuestion,
        isLastQuestion: isLastQuestion(),
        currentQuestionIndex: currentQuestionIndex + 1,
        totalQuestions: session.totalQuestions
      });
      
      // Salva risposta localmente (senza chiamata al server)
      submitAnswer(currentQuestion.id, selectedAnswer, timeSpentOnQuestion);
      
      if (isLastQuestion()) {
        console.log('🏁 [DEBUG] Ultima domanda - preparando invio finale');
        
        // Salva l'ultima risposta e invia immediatamente
        const allAnswers = getAllAnswers();
        allAnswers.push({
          questionId: currentQuestion.id,
          selectedAnswerId: selectedAnswer,
          timeSpent: timeSpentOnQuestion
        });
        
        console.log('📤 [DEBUG] Invio finale quiz al backend con', allAnswers.length, 'risposte');
        
        // Completa quiz e invia tutto al backend
        const completedSession = await finishQuizWithAnswers(allAnswers);
        if (completedSession) {
          console.log('✅ [DEBUG] Quiz completato con successo:', completedSession.id);
          
          // Calcola le risposte errate localmente per evitare chiamate API
          const wrongAnswers = calculateWrongAnswers(allAnswers, session.questions);
          
          // Passa tutti i dati locali alla pagina Results
          navigate(`/results/${completedSession.id}`, {
            state: {
              sessionData: completedSession,
              allAnswers: allAnswers,
              wrongAnswers: wrongAnswers,
              totalTimeSpent: timeSpent
            }
          });
        } else {
          // Se la sessione è già completata, naviga direttamente ai risultati
          console.log('⚠️ [DEBUG] Sessione già completata, navigo direttamente ai risultati');
          navigate(`/results/${session.id}`);
        }
      } else {
        console.log('➡️ [DEBUG] Passando alla prossima domanda');
        nextQuestion();
        const nextQuestionData = getCurrentQuestion();
        if (nextQuestionData) {
          setSelectedAnswer(getSelectedAnswerForQuestion(nextQuestionData.id));
        } else {
          setSelectedAnswer(null);
        }
        setQuestionStartTime(Date.now());
      }
    } catch (error) {
      console.error('❌ [ERROR] Errore completamento quiz:', error);
    }
  };

  const handlePrevious = () => {
    previousQuestion();
    const currentQuestion = getCurrentQuestion();
    if (currentQuestion) {
      setSelectedAnswer(getSelectedAnswerForQuestion(currentQuestion.id));
    } else {
      setSelectedAnswer(null);
    }
    setQuestionStartTime(Date.now());
  };

  if (isLoading) {
    return <QuizLoading />;
  }

  if (!session) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, rgba(239, 246, 255, 0.8) 0%, rgba(219, 234, 254, 0.6) 50%, rgba(238, 242, 255, 0.8) 100%)'
        }}
      >
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Sessione non trovata</h3>
          <p className="text-gray-600 mb-4">La sessione quiz richiesta non esiste o è scaduta.</p>
          <button
            onClick={() => navigate('/subjects')}
            className="btn-primary"
          >
            Torna alle Materie
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, rgba(239, 246, 255, 0.8) 0%, rgba(219, 234, 254, 0.6) 50%, rgba(238, 242, 255, 0.8) 100%)'
        }}
      >
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/subjects')}
            className="btn-primary"
          >
            Torna alle Materie
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = getCurrentQuestion();
  if (!currentQuestion) {
    return <QuizLoading />;
  }

  const progress = getProgress();

  return (
    <div 
      className="quiz-page h-screen w-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-blue-25 to-indigo-50"
      style={{
        overflow: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        maxHeight: '100vh',
        maxWidth: '100vw',
        background: 'linear-gradient(135deg, rgba(239, 246, 255, 0.8) 0%, rgba(219, 234, 254, 0.6) 50%, rgba(238, 242, 255, 0.8) 100%)'
      }}
    >
      {/* Topbar fissa con menu */}
      <div className="fixed top-0 left-0 right-0 z-30">
        <TopBar />
      </div>

      {/* Barra di avanzamento fissa */}
      <div className="fixed top-16 left-0 right-0 z-20 bg-white border-b border-gray-200">
        <QuizHeader 
          subjectName={session.subjectName || 'Materia'}
          progress={progress}
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={session.totalQuestions}
          timeSpent={timeSpent}
        />
      </div>

      {/* Box fisso per il testo della domanda */}
      <div 
        className="fixed left-0 right-0 z-10"
        style={{ 
          top: 'calc(14vh + 80px)', // Responsive + spazio per TopBar
          height: '20vh', // Responsive: 20% dell'altezza viewport
        }}
      >
        <div className="h-full">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-lg h-full" style={{ marginLeft: '8px', marginRight: '8px' }}>
            <div className="question-box-padding h-full flex items-center">
              <h2 
                className="text-gray-900 font-semibold leading-relaxed break-words w-full text-center"
                style={{
                  fontSize: 'clamp(14px, 4vw, 18px)',
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                  textJustify: 'inter-word'
                }}
              >
                {currentQuestion.text}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Riquadro fisso per le risposte con scroll */}
      <div 
        className="fixed left-0 right-0 z-10 bg-transparent"
        style={{ 
          top: 'calc(38vh + 84px)', // Responsive + spazio per TopBar
          height: 'calc(100vh - 38vh - 15vh - 64px)', // Corretto: spazio rimanente - TopBar
          marginLeft: '8px', // Piccolo margine laterale sinistro
          marginRight: '8px', // Piccolo margine laterale destro
          maxHeight: 'calc(100vh - 38vh - 15vh - 64px)' // Previene overflow
        }}
      >
        <div 
          className="h-full overflow-y-auto"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            maxHeight: '100%',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <QuizAnswers 
            question={currentQuestion}
            selectedAnswer={selectedAnswer}
            onAnswerSelect={handleAnswerSelect}
          />
        </div>
      </div>

      {/* Bottoni navigazione fissi */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200">
        <QuizNavigation 
          onPrevious={handlePrevious}
          onNext={handleNext}
          canGoPrevious={!isFirstQuestion()}
          canGoNext={selectedAnswer !== null}
          isLastQuestion={isLastQuestion()}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};